import {
  AppointmentSettingType,
  Gender,
  IMessageCode,
  MessageCode,
  QueryOptions, ReasonType,
  RouteQueryParamsArgs,
  Schema, State,
  UrlPaths
} from "@mindpath/shared";
import {Signal} from "@angular/core";
import {DateTime} from "luxon";


export const IDontSeeMyPlanNames= ['I do not see my plan', 'i dont see my plan', 'i don\'t see my plan'];

export const ValidStates = [State.AZ, State.CA, State.FL, State.NC, State.SC, State.TX] as const;
export const BookingSteps = ['eligibility', 'data-check', 'demographics-1', 'demographics-2', 'otp', 'insurance', 'insurance-cards', 'final'] as const;
export const PrefillSteps = ['location', 'type', 'condition', 'age', 'setting', 'insurance', 'insurancePlan'] as const;
export const InvalidDeviceCodes: readonly string[] = [
  MessageCode.NTF0001.code!,
  MessageCode.NTF0002.code!,
  MessageCode.NTF0003.code!,
  MessageCode.NTF0004.code!,
];

/** A title and plain-text message (`\n` breaks, `{support:key}` placeholders) for the stop dialog. */
export interface ErrorDisplay {
  readonly title: string;
  readonly message: string;
}

/**
 * Copy for the BSR codes -- the ones the browser raises, which the client therefore owns. Every other code
 * belongs to the API, which sends its own copy, and that wins here (`resolveErrorCopy`). Nothing else gets an
 * entry: a server code raised locally passes `options.copy` at the raise site instead, so the wording sits
 * next to the condition that produced it. BSR0000 is the fallback for any code without copy from anywhere.
 */
export const ClientErrorDisplays: Readonly<Record<string, ErrorDisplay>> = {
  [MessageCode.Unknown_Browser_Exception.code!]: {
    title: 'An Error Occurred',
    message: 'An error occurred while processing the request. Please contact support at {support:default} for assistance.',
  },
  [MessageCode.Browser_Request_Dropped.code!]: {
    title: 'Unable To Connect',
    message: 'The request could not be completed. Please check the internet connection and try again, or contact support at {support:default} for assistance.',
  },
  [MessageCode.Invalid_Parameters_Received.code!]: {
    title: 'Unable To Retrieve Appointment',
    message: 'The appointment link is missing required information. Please contact support at {support:default} for assistance.',
  },
};

/**
 * Copy for the server-owned codes the client also raises on its own, where no response body can supply it.
 * Each one is passed as `options.copy` at its raise site; they are gathered here only so the wording stays
 * reviewable in one place and matches the API's catalog for the same code.
 */
export const LocallyRaisedDisplays = {
  /** The slot is gone, or was never in the response: `appointmentAvailableGuard`. */
  slotTaken: {
    title: 'Appointment No Longer Available',
    message: 'The appointment selected is no longer available.\n\nPlease select another time.',
  },
  /** Read off the plan's `bookingStatusId`, not an error: `EligibilityComponent`. */
  planNeedsVerification: {
    title: 'Additional Information Required',
    message: 'Call {support:needsVerificationEligibility} to verify the insurance.',
  },
  planCannotSchedule: {
    title: 'Additional Information Required',
    message: 'Online scheduling is currently not available for this plan. Call {support:doNotScheduleEligibility} to schedule the appointment. Be sure to have the insurance information ready.',
  },
  /** The client-side expiry ticker, before the API is asked: `DeviceVerificationComponent`. */
  codeExpired: {
    title: 'Code Expired',
    message: 'The code that was sent has expired. Please request a new code.',
  },
  /** No token to attach, so the request never leaves: `tokenInjectionInterceptor`. */
  sessionExpired: {
    title: 'Session Expired',
    message: 'The session has expired. Please start a new session to continue.',
  },
} as const satisfies Readonly<Record<string, ErrorDisplay>>;

/** Stop-dialog button labels. The client chooses the button by code; the API never sends one. */
export const ErrorActionLabels = Object.freeze({
  ViewAvailableTimes: 'View Available Times',
  RequestNewCode: 'Request New Code',
  StartNewSession: 'Start New Session',
  ReturnToClinicians: 'Return To Clinicians',
});

export const DefaultSupportLinkClass = 'pk_dialog_intake';

/** Piwik tracks support-link clicks by class. The eligibility stops have always used their own. */
export const SupportLinkClasses: Readonly<Record<string, string>> = {
  [MessageCode.ITK0011.code!]: 'pk_insurance-details_intake',
  [MessageCode.ITK0012.code!]: 'pk_insurance-details_intake',
};


export interface SchedulerDataSnapshot {
  appt: SelectedAppointmentInfo|null,
  demographics: DemographicsInfo | null,
  meta: MetaInfo,
  insurance: { primary: InsuranceInfo | null, secondary: InsuranceInfo | null },
}

export interface PageStepsMap {
  prefill: typeof PrefillSteps[number],
  find: never,
  book: typeof BookingSteps[number]
}

export type DeepNonNullable<T> = {
  [P in keyof T]-?: T[P] extends (infer U)[] ? DeepNonNullable<U>[] :
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    T[P] extends (...args: any[]) => any ? T[P] :
      T[P] extends object ? DeepNonNullable<T[P]> : T[P];
};
export type DeepPartial<T> = {
  [P in keyof T]?: T[P] extends (infer U)[] ? DeepPartial<U>[] : T[P] extends object ? DeepPartial<T[P]> : T[P];
};

export interface MetaInfo {
  error?: IMessageCode | null,
  section: 'prefill' | 'find' | 'book';
  resumableTo?: {
    section: 'prefill',
    step: PageStepsMap['prefill']
  } | {
    section: 'find',
    step?: undefined;
  }
  prefill: {
    step: PageStepsMap['prefill']
  }
  booking: {
    id: string,
    eligibility: { id: string, checked: boolean }[],
    step: PageStepsMap['book'],
    auth?: {
      request?: {
        at: DateTime<true>,
        deviceType: 'sms' | 'email' | 'phone',
        destination: string,
        usingIntakeEmail?: boolean;
        intakeEmail?: string;
      }
      token: string
      tokenExpiresAt: DateTime<true>,
      multiFactorComplete: boolean,
    },
    completionData?: {
      formsLink: string,
      attachments: AttachmentToken[]
    }
  }

}

export interface SearchInfo {
  conditionId: number | null | undefined,
  clinicianTypePref: ReasonType.Therapy|ReasonType.Psychiatry | null | undefined,
  settingPref: AppointmentSettingType.InPerson|AppointmentSettingType.Telehealth | null | undefined,
  age: 'Child' | 'Adult' | 'Senior' | null | undefined,
  languagePref: string | null | undefined,
  genderPref: Gender | null | undefined,
  timeOfDayPref: 'Morning' | 'Afternoon' | null | undefined,
  dayOfWeekPref: 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday' | null | undefined,
  soonestWeekOut: 0 | 1 | 2 | 4 | null | undefined;
}

export interface InsuranceInfo {
  readonly id?: number;
  readonly planId?: number;
  readonly groupNumber?: string;
  readonly policyNumber?: string;
  readonly holder?: {
    name?: string;
    dob?: DateTime<true>;
  };
  readonly cashSelfPayAttested?: boolean|null;
  readonly isCashSelfPay?: boolean;
}

export interface GeneralInfoDemographics {
  readonly firstName?: string;
  readonly lastName?: string;
  readonly dob?: DateTime<true>;
  readonly sex?: Gender;
  readonly state?: State;
}

export interface ContactInfoDemographics {
  readonly email?: string;
  readonly phone?: string;
  readonly address?: Schema<'Address'>
  readonly otpDevice?: 'sms' | 'email' | 'phone';
  readonly intakeEmail?: string;
  readonly otpDestination?: string;
}

export interface DemographicsInfo extends GeneralInfoDemographics, ContactInfoDemographics {
}

export interface SelectedAppointmentInfo {
  readonly clinician: Schema<'ClinicianDetailedInfo'>;
  readonly slot: Schema<'TimeSlot'>;
  readonly originalSetting: Schema<'TimeSlot'>['setting'];
  readonly chosenSetting: AppointmentSettingType.InPerson | AppointmentSettingType.Telehealth;
  readonly date: DateTime<true>;
  readonly time: [number, number];
  readonly duration: number;
  readonly location: Schema<'LocationInfo'>;
  readonly typeOfCare: ReasonType.Therapy | ReasonType.Psychiatry;
  readonly conditionId: number;
  readonly isBooked?: boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type DeepSignalValue<T> = T extends Signal<infer U> ? DeepSignalValue<U> : T extends any[] ? DeepSignalValue<T[number]>[] : T extends Record<string, any> ? {
  [K in keyof T]: DeepSignalValue<T[K]>
} : T;
export type SignalValue<T> = T extends Signal<infer U> ? U : T;
export type EachKeyAs<TObj, TAs> = TObj extends string | number | Date | DateTime | boolean ? TAs :
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  TObj extends any[] ? EachKeyAs<TObj[number], TAs> :
    {
      [K in keyof TObj]: EachKeyAs<TObj[K], TAs>
    };
export type PropErrors<T> = EachKeyAs<T, (string[] | null)>;
type ExtractArrayType<T> = T extends (infer U)[] ? U : T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ExcludedTypes = Date | DateTime | Set<any> | Map<any, any> | string | number | boolean | null | undefined;
export type FlattenObjectKeys<T> = {
  [K in keyof T]-?: Exclude<T[K], null> extends ExcludedTypes
      ? `${K & string}` // Do not flatten if it's an excluded type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
      : Exclude<T[K], null> extends Record<string, any>
        ? `${K & string}` | `${K & string}.${FlattenObjectKeys<Exclude<T[K], null>>}` // Include both the parent key and nested keys
        : T[K] extends null
          ? never
          : Exclude<ExtractArrayType<T[K]>, null> extends unknown
            ? `${K & string}`
            : K & string;
}[keyof T];




export interface Token {
  readonly token: string;
  readonly expiresAt: number;
}

export interface AttachmentToken extends Token {
  readonly data: {
    forPrimary: boolean;
    side: 'front' | 'back';
  }
}

export interface QueryBuilder<P extends UrlPaths, F extends Partial<QueryOptions<P>> = QueryOptions<P>> {
  and: <T extends keyof F>(field: T, value: F[T]) => QueryBuilder<P, Exclude<F, T>>;
  assemble: () => QueryOptions<P>;
}

export type QueryKeyValPairs<P extends UrlPaths> = [keyof RouteQueryParamsArgs<P>, RouteQueryParamsArgs<P>[keyof RouteQueryParamsArgs<P>]][];
